import React from 'react';
import connect from 'Shared/connect';
import { styled } from '@glitz/react';
import { ToggleableField } from 'Shared/Accordion';
import { formatPrice } from 'Shared/number-format';
import VoucherViewModel from './VoucherViewModel.type';
import CheckoutUpdateResultType from '../Pages/Checkout/CheckoutUpdateResult.type';
import FeedbackWrapper from 'Shared/FeedbackWrapper';
import { Close16x16 } from 'Shared/Icons/Close';
import FeedbackButton, { ButtonTheme } from 'Shared/Button/Feedback';
import * as style from 'Shared/Style';
import { VoucherType } from 'Checkout/action-creators';

const Remove = styled(FeedbackWrapper, {
  marginRight: '6px'
});

const Item = styled.div({
  position: 'relative',
  display: 'flex',
  justifyContent: 'space-between',
});

const CodeItem = styled.span({
  fontWeight: 'bold',
});

const VoucherItemWrap = styled.div({
  display: 'flex',
  alignItems: 'center',
})

const VoucherItemPrice = styled.span({
  flexGrow: 1,
  paddingRight: '12px'
})

const VoucherItemInvalid = styled.span({
  paddingLeft: '12px',
  color: style.colors.negativeColors.negative,
})

const VoucherListWrap = styled.div({
  display: 'flex',
  flexDirection: 'column',
})

type RequiredPropType = {
  header: string;
  buttonText: string;
  vouchers: VoucherViewModel[];
  vouchersInCart?: VoucherViewModel[];
  cartTotal: number;
  addVoucher: (code: string, type: VoucherType) => Promise<CheckoutUpdateResultType>;
  removeVoucher?: (value: string, type: VoucherType) => Promise<void>;
  labels: {
    genericError: string,
  },
  voucherType: VoucherType
};

type ConnectedPropType = {
  currency: string;
  culture: string;
};

type PropType = RequiredPropType & ConnectedPropType;

type StateType = {
  isOpen?: boolean;
  errorMessages?: {};
  isLoading: boolean;
};

class Voucher extends React.Component<PropType, StateType> {
  constructor(props: PropType) {
    super(props);
    this.state = {
      isOpen: true,
      errorMessages: null,
      isLoading: false,
    };
  }

  async removeVoucher(code: string) {
    const action = async () => {
      await this.props.removeVoucher(code, this.props.voucherType);
      return Promise.resolve(null);
    };

    this.setState({ errorMessages: null });

    try {
      const promise = action();
      await promise;
    } catch (e) {
      console.log(e);
    }
  }

  async addVoucher(id: string, amount: number) {
    try {
      this.setState({ isLoading: true });
      const { success, message } = await this.props.addVoucher(id, this.props.voucherType);

      if (success) {
        this.setState({ isLoading: false });
        return Promise.resolve();
      } else {
        this.setState({ errorMessages: { [id]: message }, isLoading: false });
        console.log(this.state.errorMessages);
        return Promise.reject(null);
      }
    } catch (e) {
      console.log(e);
      this.setState({ errorMessages: { "0": this.props.labels.genericError }, isLoading: false });
      return Promise.reject(null);
    }
  }

  onToggle = () => this.setState({ isOpen: !this.state.isOpen });
  render() {
    const { vouchers } = this.props;

    const Header =
      this.props.vouchersInCart?.length > 0 ? (
        this.props.header + " (" + this.props.vouchersInCart.length + ")"
      ) : (
        this.props.header
      );

    const voucherList = this.props.vouchersInCart.map((voucher, i) => {
      const data = (
        <Item key={voucher.id}>
          <Remove flexInnerDiv feedback={() => this.removeVoucher(voucher.id)}>
            <Close16x16 />
          </Remove>
          <CodeItem>
            {formatPrice(voucher.amount, this.props.currency, this.props.culture)}
          </CodeItem>
        </Item>
      );
      return data;
    });

    return (
      <ToggleableField iconWidth='16px' upperCaseHeading headerFontSize='14px' noBorder extraPadding header={Header} isOpen={this.state.isOpen} onToggle={this.onToggle}>
        <div>
          <>
            {vouchers.map((voucher, i) => {
              return (
                <VoucherItemWrap
                  key={voucher.id}
                  css={{
                    ...(this.state.isLoading && {
                      pointerEvents: 'none',
                    }),
                    ...(this.state.isOpen && {
                      marginTop: '14px'
                    })
                  }}
                >
                  <VoucherItemPrice>
                    {formatPrice(voucher.amount, this.props.currency, this.props.culture)}
                    {this.state.errorMessages && this.state.errorMessages[voucher.id] && <VoucherItemInvalid>
                      {this.state.errorMessages[voucher.id]}
                    </VoucherItemInvalid>}
                  </VoucherItemPrice>
                  <FeedbackButton
                    onClick={() => this.addVoucher(voucher.id, this.props.voucherType)}
                    theme={ButtonTheme.BlackToWhite}
                  >
                    {this.props.buttonText}
                  </FeedbackButton>
                </VoucherItemWrap>
              );
            })}
          </>
          {voucherList && voucherList.length > 0 &&
            <VoucherListWrap
              css={{
                ...(this.state.isOpen && {
                  marginTop: '8px'
                })
              }}
            >
              {voucherList}
            </VoucherListWrap>
          }
        </div>
      </ToggleableField>
    );
  }
}

export default connect(state => ({
  currency: state.appShellData.market.currency,
  culture: state.appShellData.culture,
}))(Voucher);
